import { Children, Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import gsap from "gsap";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { MOTION, prefersReducedMotion } from "@/lib/motion";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type NavGroupItem = { id: string; label: string; icon: LucideIcon; path: string; permKey?: string; badge?: ReactNode };
export type NavGroupSection = { id: string; label?: string; items: NavGroupItem[] };

/** The line starts just under the group's own icon — it comes from the group,
 *  not from the list. Half the icon, plus a breath. */
const TRAIL_START = 9;
/** CornerDownRight at half size, drawn as one stroke with the run above it
 *  instead of as a glyph sitting at its end: a 1px CSS line is crisp and the
 *  same stroke inside a scaled <svg> is spread over two columns, so the two
 *  never match at any offset. The corner radius and arrowhead follow lucide's
 *  geometry halved; the run's own x is measured off the group's icon. */
const TRAIL_CORNER = 2;

// One row per group, its destinations in a panel anchored beside it. Replaces
// the inline accordion: expanding a group used to push every section below it
// down the rail, so the position of a destination depended on which other
// groups happened to be open. Collapsed and expanded rails now share this one
// path — the flyout is the only place sub-items ever render.
/** A named block of the rail that folds away. The label was a caption; making it
 *  the control means the whole group can be put out of sight, which is what a
 *  rail with seven of them needs. Folded state is per session, not persisted:
 *  a group hidden last week should not hide where you are today. */
/** Height-and-fade for anything that folds. Kept in one place so every fold in
 *  the rail reads as the same gesture, and so the reduced-motion check is not
 *  re-decided per call site. Opening clears the inline height on arrival: a
 *  group pinned to the pixels it measured would not grow with its content. */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const settle = () => gsap.set(el, { height: open ? "auto" : 0, opacity: open ? 1 : 0 });

    if (!mounted.current) {
      mounted.current = true;
      settle();
      return;
    }
    gsap.killTweensOf(el);
    if (prefersReducedMotion()) {
      settle();
      return;
    }
    if (open) {
      gsap.fromTo(
        el,
        { height: 0, opacity: 0 },
        { height: "auto", opacity: 1, duration: 0.34, ease: MOTION.ease, clearProps: "height" },
      );
    } else {
      gsap.to(el, { height: 0, opacity: 0, duration: MOTION.exitDuration, ease: MOTION.exitEase });
    }
  }, [open]);

  return <div ref={ref} className="overflow-hidden">{children}</div>;
}

export function NavSection({ label, collapsed, children }: { label: string; collapsed: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  if (Children.toArray(children).length === 0) return null;
  if (collapsed) return <>{children}</>;
  return (
    <div className="has-[[data-nav-section-body]:empty]:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1 rounded-md px-2.5 py-2 text-[12px] font-medium text-mute transition-colors hover:text-ink-800"
      >
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown className={cn("h-3 w-3 shrink-0 opacity-60 transition-transform duration-200", !open && "-rotate-90")} />
      </button>
      <Collapse open={open}><div data-nav-section-body className="space-y-1.5">{children}</div></Collapse>
    </div>
  );
}

export function NavGroup({
  icon: Icon, label, sections, active, collapsed, isItemActive, onSelect, dataTour,
}: {
  icon: LucideIcon;
  label: string;
  sections: NavGroupSection[];
  active: boolean;
  collapsed: boolean;
  isItemActive: (item: NavGroupItem) => boolean;
  onSelect: (path: string) => void;
  dataTour?: string;
}) {
  // Hover opens the flyout; click still does too. The close is delayed so the
  // pointer can cross the gap between the row and the panel without the panel
  // vanishing under it, and entering the panel cancels the pending close.
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const openNow = () => { cancelClose(); setOpen(true); };
  const closeSoon = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };
  useEffect(() => cancelClose, []);

  // Open when one of its own destinations is showing, so arriving by URL leaves
  // the group unfolded rather than hiding where you are.
  const [expanded, setExpanded] = useState(active);
  useEffect(() => { if (active) setExpanded(true); }, [active]);

  // The open group draws a line down from its icon, ending in an arrow at the
  // destination you are on: the rail says where you are without the row having
  // to carry the whole path. It is the arrow's own run — with none of these
  // destinations showing there is nothing to point at, so nothing is drawn.
  const headRef = useRef<HTMLButtonElement>(null);
  const rows = useRef(new Map<string, HTMLButtonElement>());
  const [arrowAt, setArrowAt] = useState<number | null>(null);
  // Where the run is drawn to right now. It chases arrowAt so the line grows
  // into its new length; the path's own numbers change per frame, which no CSS
  // transition can do for us.
  const [drawnAt, setDrawnAt] = useState<number | null>(null);
  const drawn = useRef({ at: 0 });
  const [hovered, setHovered] = useState<string | null>(null);
  const items = sections.flatMap((s) => s.items);
  // Where the arrow rests: the destination showing, or the group's own entry
  // when you are deeper inside it than the list goes. Hovering lends it to the
  // row under the pointer, so the rail answers "and this one?" before the click.
  const currentId = items.find((item) => isItemActive(item))?.id ?? (active ? items[0]?.id ?? null : null);
  const targetId = hovered ?? currentId;

  useLayoutEffect(() => {
    const head = headRef.current;
    if (!head || collapsed) return;
    // Rects, not offsetTop: those are rounded to whole pixels, and a run that
    // ends three quarters of a pixel below the row's middle shows it.
    const measure = () => {
      const row = targetId ? rows.current.get(targetId) : null;
      if (!row) {
        setArrowAt(null);
        return;
      }
      const box = (head.parentElement as HTMLElement).getBoundingClientRect();
      const seat = row.getBoundingClientRect();
      setArrowAt(seat.top - box.top + seat.height / 2);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(head.parentElement as Element);
    return () => observer.disconnect();
  }, [collapsed, expanded, targetId, sections]);

  useEffect(() => {
    if (arrowAt === null) {
      setDrawnAt(null);
      return;
    }
    if (drawnAt === null || prefersReducedMotion()) {
      drawn.current.at = arrowAt;
      setDrawnAt(arrowAt);
      return;
    }
    const tween = gsap.to(drawn.current, {
      at: arrowAt,
      duration: MOTION.duration,
      ease: MOTION.ease,
      onUpdate: () => setDrawnAt(drawn.current.at),
    });
    return () => { tween.kill(); };
    // drawnAt is the tween's own output; following it here would restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrowAt]);

  // The run hangs off the group's own icon: its x is the icon's middle and it
  // starts just under it, both measured rather than assumed, so a change to the
  // row's padding or the icon's size carries the line with it.
  const head = headRef.current;
  const box = head?.parentElement?.getBoundingClientRect();
  const headBox = head?.getBoundingClientRect();
  const iconBox = head?.querySelector("svg")?.getBoundingClientRect();
  const trailTop = box && headBox ? headBox.top - box.top + headBox.height / 2 + TRAIL_START : 0;
  const trailX = box && iconBox ? iconBox.left - box.left + iconBox.width / 2 : 0;

  if (items.length === 0) return null;

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        data-tour={dataTour}
        aria-label={label}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        className={cn(
          "w-full flex items-center justify-center rounded-xl p-2 text-[13px] transition-colors",
          active ? "bg-black/[0.06] font-medium text-ink-900 dark:bg-white/[0.09]" : "text-ink-700 hover:bg-black/[0.04] hover:text-ink-900 dark:hover:bg-white/[0.06]",
        )}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
      </button>
    </DropdownMenuTrigger>
  );

  // Expanded rail: the group unfolds in place. A flyout is for an icon rail,
  // where there is nowhere to unfold into.
  if (!collapsed) {
    return (
      <div className="relative">
        {/* Outside the fold, so the line can start at the icon instead of at
            the top of the list, and clear of the row spacing's margins. */}
        {expanded && drawnAt !== null && (
          <svg
            aria-hidden
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="pointer-events-none absolute inset-0 h-full w-full overflow-visible text-mute"
          >
            <path d={`M${trailX} ${trailTop} V${drawnAt - TRAIL_CORNER} a${TRAIL_CORNER} ${TRAIL_CORNER} 0 0 0 ${TRAIL_CORNER} ${TRAIL_CORNER} h3`} />
            <path d={`M${trailX + 3.5} ${drawnAt - 2.5} L${trailX + 6} ${drawnAt} L${trailX + 3.5} ${drawnAt + 2.5}`} />
          </svg>
        )}
        <button
          ref={headRef}
          data-tour={dataTour}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className={cn(
            "w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-[13px] transition-colors",
            active ? "font-medium text-ink-900" : "text-ink-700 hover:bg-black/[0.04] hover:text-ink-900 dark:hover:bg-white/[0.06]",
          )}
        >
          <Icon className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1 text-left">{label}</span>
          <ChevronDown className={cn("h-3.5 w-3.5 flex-shrink-0 text-mute transition-transform duration-200", expanded && "rotate-180")} />
        </button>
        <Collapse open={expanded}>
          <div className="mt-1.5 space-y-1.5 pl-[26px]">
            {sections.map((section) => (
              <Fragment key={section.id}>
                {section.label && (
                  <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-mute">{section.label}</p>
                )}
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    ref={(el) => {
                      if (el) rows.current.set(item.id, el);
                      else rows.current.delete(item.id);
                    }}
                    onMouseEnter={() => setHovered(item.id)}
                    onMouseLeave={() => setHovered((id) => (id === item.id ? null : id))}
                    onClick={() => onSelect(item.path)}
                    className={cn(
                      "w-full flex items-center rounded-md px-2.5 py-2 text-left text-[13px] transition-colors",
                      isItemActive(item)
                        ? "bg-black/[0.06] font-medium text-ink-900 dark:bg-white/[0.09]"
                        : "text-ink-700 hover:bg-black/[0.04] hover:text-ink-900 dark:hover:bg-white/[0.06]",
                    )}
                  >
                    <span className="flex-1">{item.label}</span>
                    {item.badge}
                  </button>
                ))}
              </Fragment>
            ))}
          </div>
        </Collapse>
      </div>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={(next) => { cancelClose(); setOpen(next); }}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={8}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="z-[80] w-64 max-h-[70vh] overflow-y-auto"
      >
        <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </DropdownMenuLabel>
        {sections.map((section, i) => (
          <Fragment key={section.id}>
            {i > 0 && <DropdownMenuSeparator />}
            {section.label && (
              <DropdownMenuLabel className="pt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                {section.label}
              </DropdownMenuLabel>
            )}
            {section.items.map((item) => (
              <DropdownMenuItem
                key={item.id}
                onClick={() => onSelect(item.path)}
                className={cn(isItemActive(item) && "bg-black/[0.06] font-medium text-ink-900 dark:bg-white/[0.09]")}
              >
                <item.icon className="mr-2 h-3.5 w-3.5 flex-shrink-0" />
                <span className="flex-1 text-left">{item.label}</span>
                {item.badge}
              </DropdownMenuItem>
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function RailLink({
  icon: Icon, label, active, collapsed, onSelect, badge, tooltip, dataTour,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  collapsed: boolean;
  onSelect: () => void;
  badge?: ReactNode;
  tooltip?: string;
  dataTour?: string;
}) {
  const btn = (
    <button
      data-tour={dataTour}
      onClick={onSelect}
      className={cn(
        "relative w-full flex items-center gap-2 rounded-md text-[13px] transition-colors",
        collapsed ? "justify-center p-2.5" : "px-2.5 py-2",
        active
          ? "bg-black/[0.06] font-medium text-ink-900 dark:bg-white/[0.09]"
          : "text-ink-700 hover:bg-black/[0.04] hover:text-ink-900 dark:hover:bg-white/[0.06]",
      )}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      {!collapsed && <span>{label}</span>}
      {badge && <span className={cn("flex items-center gap-1", collapsed ? "absolute right-0.5 top-0.5" : "ml-auto")}>{badge}</span>}
    </button>
  );
  if (!collapsed) return btn;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side="right">{tooltip ?? label}</TooltipContent>
    </Tooltip>
  );
}
